import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { User } from '@app/models/user';
import { HttpService } from './http.service';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { environment } from '@env/environment';
import { Router, UrlTree } from '@angular/router';
import { switchMap, map, catchError, tap, filter, take } from 'rxjs/operators';
import { RouteDataService } from './route-data.service';
// https://jasonwatmore.com/post/2019/08/06/angular-8-role-based-authorization-tutorial-with-example

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private currentUser: BehaviorSubject<User> = new BehaviorSubject( null );
  public currentUser$: Observable<User> = this.currentUser.asObservable();
  private isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject( false );
  public isAuthenticated$: Observable<boolean> = this.isAuthenticated.asObservable();
  private encryptionKey: BehaviorSubject<CryptoKey> = new BehaviorSubject( null );
  public encryptionKey$: Observable<CryptoKey> = this.encryptionKey.asObservable();
  private urlLogin: UrlTree;
  private urlLogout: UrlTree;

  // constructor
  constructor(
    private routeDataService: RouteDataService,
    private httpService: HttpService,
    private router: Router,
    private http: HttpClient
  ) { 

    // set the login url ( we use this in other modules/components when we need to route to the login page )
    this.urlLogin = this.router.createUrlTree([ 'account', 'login' ]);
    this.urlLogout = this.router.createUrlTree([ 'account', 'logout' ]);
    this.loginEnvironment();
  }
  
  private loginEnvironment(){
    if( environment.bypass_login == true && environment.production == false )
    {
      this.currentUser.next( User.getTestUser());
      this.isAuthenticated.next( true );
    }
  }

  // get the login url
  public getUrlLogin(): UrlTree {
    return this.urlLogin;
  }

  // get the logout url
  public getUrlLogout(): UrlTree {
    return this.urlLogout;
  }

  // login via api
  public login( username: string, password: string ) {
    const urlApi = this.httpService.createUrl([
      'auth',
      'login'
    ], {}, { host: environment.host_api_default });
    return this.httpService.post<any>( urlApi, { username, password }, {
       //observe: 'response' as 'response'
       withCredentials: true
    }).pipe(
      map( authResult => {
        var keyBytes;
        if (authResult.encryptionKey) {
            try {
                const binaryString = atob(authResult.encryptionKey);
                keyBytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    keyBytes[i] = binaryString.charCodeAt(i);
                }
            } catch {}
        }

        // login successful if there's a token in the response
        if( authResult && authResult.user) {
          this.currentUser.next( authResult.user );
          this.isAuthenticated.next( true );

          if (keyBytes) {
            const key = crypto.subtle.importKey(
              "raw",
              keyBytes,
              { name: "AES-GCM", length: 256 },
              false,
              ["encrypt", "decrypt"]
            ).then(key => this.encryptionKey.next( key ))
             .catch(() => {});
          }
        }
        return authResult.user;
      })
    )
  }

  // logout the user
  public logout(): void {

    const urlApi = this.httpService.createUrl([
      'auth',
      'logout'
    ], {}, { host: environment.host_api_default });
    
    this.httpService.post<any>( urlApi, {}).pipe(
      map( result => {
        // login successful if there's a token in the response
        console.log('Logged out from server');
      })
    ).subscribe({
      next: () => {
        // continue to clear local authentication
        this.handleLocalLogout();
      },
      error: ( err: HttpErrorResponse ) => {
        console.error(`Error logging out from server: ${err.message}`); 
        this.handleLocalLogout();
      },
      complete: () => {
      }
    });

  }

  // handle local logout without calling the server
  private handleLocalLogout(): void {
    // remove user from local storage to logout
    localStorage.removeItem('currentUser');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');

    this.routeDataService.clearRouteData();
    this.currentUser.next( null );
    this.encryptionKey.next( null );
    this.isAuthenticated.next( false ); // tell all observers that user is no longer authenticated
  }

  // get access token for current user
  public getAccessToken(): string {
    return 'token';
  }

  // refresh token
  public refreshToken() {
    const urlApi = this.httpService.createUrl([
      'auth',
      'refresh_token'
    ], {}, { host: environment.host_api_default });

    const httpOptions = {
      withCredentials: true,
    };

    // display current jwt token
    console.warn('Refreshing token from ', urlApi);

    return this.httpService.post<any>(urlApi, {}, httpOptions).pipe(
      tap(data => {
        let user = this.currentUser.getValue();
        if (data.token) {
          user.token = data.token;
          this.currentUser.next(user);
        } else {
          this.logout();
        }
      })
    );
  }

  // check if user has any of the specified roles
  public userHasRole( roles: string[] )
  {
    let hasRole: boolean = false;
    let user = this.getCurrentUser();

    if (user != null) {
      let userRoles = user.roles;
      userRoles.forEach( role => {
        if( roles.indexOf( role.role_name ) !== -1 )
        {
          hasRole = true;
        }
      });
    }
    return hasRole;
  }

  // get the current user
  public getCurrentUser(): User {
    return this.currentUser.getValue();
  }

  // set the current user
  public setCurrentUser(user: User): void {
    this.currentUser.next( user );
  }

  // check if user is authenticated
  public userIsAuthenticated(): boolean {
    return this.isAuthenticated.getValue();
  }

  public getUserEncryptionKey(): CryptoKey {
    return this.userIsAuthenticated() ? this.encryptionKey.getValue() : null;
  }

  // set user authenticated status
  public setUserAuthenticated(isAuthenticated: boolean) : void {
    this.isAuthenticated.next( isAuthenticated );
  }
  
}



@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
  
  constructor(private authService: AuthService) {
    // do nothing here
  }

  private isRefreshing: boolean = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  // add token to request
  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        'x-access-token': token
      },
      withCredentials: true
    });
  }

  // mark request as retried
  private markAsRetried(req: HttpRequest<any>): HttpRequest<any> {
    return req.clone({
      setHeaders: { 'x-retried': 'true' }
    });
  }

  // handle 401 error by refreshing token
  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    // prevent infinite retry
    if (req.headers.has('x-retried')) {
      this.authService.logout();
      return throwError(() => new Error('Already retried request; logged out.'));
    }

    // check if token refresh is already in progress
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap(response => {
          this.isRefreshing = false;

          const newToken = response.token;
          this.refreshTokenSubject.next(newToken);

          const user = this.authService.getCurrentUser();
          if (user) {
            user.token = newToken;        
            this.authService.setCurrentUser(user);
          }

          return next.handle(
            this.addToken(
              this.markAsRetried(req),
              newToken
            )
          );
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.authService.logout();
          return throwError(() => err);
        })
      );
    }

    // if refreshing is already in progress, wait until it's done
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token =>
        next.handle(
          this.addToken(
            this.markAsRetried(req),
            token!
          )
        )
      )
    );
  }

  // intercept all http requests; if it fails with 401, try to refresh the token
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Never intercept the refresh call itself
    if (req.url.includes('/auth/')) {
      return next.handle(req.clone({ withCredentials: true }));
    }

    // Attach access token if present
    const user = this.authService.getCurrentUser();
    if (user?.token) {
      req = this.addToken(req, user.token);
    }

    return next.handle(req).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          return this.handle401Error(req, next);
        }
        return throwError(() => err);
      })
    );
  }

}
